import React, { useState, useEffect } from 'react'
import {
    IonButton,
    IonContent,
    IonMenuButton,
    IonPage,
    useIonRouter,
} from '@ionic/react'
import { useParams } from 'react-router-dom'
import { joinClub } from './firebase'
import Icon from './Icon'
import LoadingSpinnerButton from './LoadingSpinnerButton'
import {
    useUser,
    useClub,
    useThisClub,
	type LoginMode,
	useSetShowLogin,
} from './data'
import { userLoginObject } from './util'
import './joinclub.css'
import Header from './Header'
import {PlanName} from './Plan'

type JoinClubParams = {
	clubId?: ClubId
	code?: string
}

const JoinClub = () => {

	return (
		<IonPage>
			<IonContent className="ion-padding">

				<section className="joinclub">
					<h1>
						<Icon />
						You've Been Invited to Join a Club on AutoRoster!
					</h1>
					<JoinClubContent />
				</section>

			</IonContent>
			<Header allowTeamChange={false} />
		</IonPage>
	)
}

const JoinClubContent = () => {

	const [ err, setErr ] = useState<string | null>(null)
    const [ busy, setBusy ] = useState(false)
    const [ success, setSuccess ] = useState(false)

    const user = useUser()

	const club = useClub()

	const params = useParams<JoinClubParams>()
	const clubId = params.clubId || ''
	const code = params.code || ''
	const thisClub = useThisClub(clubId)
	const router = useIonRouter()

	const goHome = () => {
		router.push('/', 'forward', 'push')
	}

    useEffect(() => {
		const inviteCode = thisClub?.inviteCodes?.find(obj => obj.code === code)

        if (!code || !clubId) {
            setErr('The invitation link you used is malformed. Please make sure it was sent correctly.')
        } else if (!thisClub?.id) {
            setErr('Club not found.')
        } else if (!inviteCode || inviteCode.expiry < Date.now()) {
            setErr('This invitation code has expired. Please ask the club to generate a new one.')
        } else if (club?.id === thisClub?.id) {
            setErr("You've already joined this club.")
        } else if (club?.id) {
            setErr("You're already part of another club: " + club.name + ". Please leave that one first, using the top-left menu.")
        } else {
            setErr(null)
        }
    }, [ code, clubId, thisClub?.id, thisClub?.inviteCodes, thisClub?.name, club?.id, club?.name ])

	if (!thisClub) {
		return (
			<p>
				Loading club...
			</p>
		)
	}

    if (success) {
        return (
            <>
                <p>
                    Welcome! You are now a member of <span className="club-name">{thisClub.name}</span>.
                </p>

                <div className="open-profile-button-container">
                    <IonMenuButton menu="profileMenu" className="open-profile-button">
                     My Profile
                    </IonMenuButton>
                </div>

				<IonButton
					expand="block"
					onClick={goHome}
				>
					Continue
				</IonButton>
            </>
        )
    }

    if (err) {
        return (
            <>
				<h4>
					{thisClub?.name}
				</h4>
                <p className="error-message">
                    {err}
                </p>
				<IonButton
					onClick={goHome}
				>
					OK
				</IonButton>
            </>
        )
    }

    const onClick = async () => {

		if (!user)
			return

		const userObject = userLoginObject(user)
		if (!userObject)
			return

        setBusy(true)

        try {
            const { data } = await joinClub({
                clubId,
                code,
				user: userObject,
            })

            console.log('result', data)

            const { ok, error } = data

            if (ok) {
                setSuccess(true)
            } else if (error) {
                setErr(error)
            }

            setBusy(false)

        } catch (err) {
            setErr("Unknown error: This request did not succeed.")
        }

        setBusy(false)
    }

    return (
        <div>
            <h4>
                {thisClub?.name}
            </h4>
            <p>
				If you join:
			</p>
			<ul>
				<li>
                    <p>
                        this club's admin will be able to view and modify your rosters, view your
                        team stats, and add/remove coaches to/from your teams. This includes the
                        ability to remove you as coach.
                    </p>
				</li>
				<li>
                    <p>
                        you'll be granted access to any <PlanName /> benefits purchased by the club.
                    </p>
				</li>
			</ul>
			<p>
				You can leave the club at any time.
			</p>

			{
				user ? (

					<LoadingSpinnerButton
						busy={busy}
						onClick={onClick}
					>
						Join {thisClub.name}
					</LoadingSpinnerButton>
				) : (
					<SignedOutJoinClubPrompt />
				)
			}

			<IonButton
				className="button-cancel"
				fill="clear"
				color="medium"
				onClick={goHome}
			>
				Cancel
			</IonButton>

		</div>
	)
}

const SignedOutJoinClubPrompt = () => {

	const setShowLogin = useSetShowLogin()

	const loginButton = (mode: LoginMode, label: string, fill: 'solid' | 'outline' | 'clear') => (
		<IonButton
			expand="block"
			fill={fill}
			onClick={() => setShowLogin(mode)}
		>
			{label}
		</IonButton>
	)

	return (
		<>
			<p className="warning">
				<b>To join this club,</b> sign back in to your AutoRoster account or create a new one.
			</p>
			{loginButton('createAccount', 'Create new account', 'solid')}
            {loginButton('signIn', 'Sign back in', 'outline')}
		</>
	)
}

export default JoinClub
